import {
  ProjectRoleHistory,
  ProjectRoleNameEditor,
} from "@/containers/Admin/ProjectRoles";
import { RegionalRoleEditor } from "@/containers/Admin/ProjectRoles/components/RegionalRole";
import { AddRegionalRoleModal } from "@/containers/Admin/ProjectRoles/components/RegionalRole/Modals";
import { useProjectRoleContext } from "@/containers/Admin/ProjectRoles/ProjectRoleProvider";
import { CONTENT_WIDTH, FieldNames, FormType, GUTTER_SIZE } from "@/enums";
import { IDictionary, IProjectRole } from "@/interfaces";
import { IRegionalProjectRole } from "@/interfaces/Project/Roles/IRegionalProjectRole";
import { useFormPropertyValueLookupProvider } from "@/providers";
import { useGetFormLookupsQuery } from "@/services";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Dropdown, Row, Space } from "antd";
import { ItemType } from "antd/es/menu/interface";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { v4 } from "uuid";

interface IProps {
  projectRole: IProjectRole;
  originalProjectRole: IProjectRole;
  handleUnsavedChanges(hasUnsavedChanges: boolean): void;
}

export const EditProjectRole = ({
  projectRole,
  originalProjectRole,
  handleUnsavedChanges,
}: IProps) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedRegion, setSelectedRegion] = useState<IDictionary>({
    key: "",
    value: "",
  });

  const intl = useIntl();
  const { data: forms } = useGetFormLookupsQuery();
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  const { updateProjectRole } = useProjectRoleContext();

  const regions = useMemo((): ItemType[] => {
    if (!forms?.intake?.region) return [];

    return Object.keys(forms.intake.region).map((key) => ({
      key,
      label: getPropertyValueDisplay(FormType.Intake, FieldNames.Region, key),
    }));
  }, [forms?.intake?.region, getPropertyValueDisplay]);

  const originalRegions = useMemo((): ItemType[] => {
    if (!forms?.intake?.region) return [];

    return Object.keys(forms.intake.region).map((key) => ({
      key,
      label: getPropertyValueDisplay(FormType.Intake, FieldNames.Region, key),
    }));
  }, [forms?.intake?.region, getPropertyValueDisplay]);

  const hasRegionalChanges = useMemo(
    (): boolean =>
      JSON.stringify(projectRole?.regionalProjectRoles ?? []) !==
        JSON.stringify(originalProjectRole?.regionalProjectRoles ?? []) ||
      JSON.stringify(regions) !== JSON.stringify(originalRegions),
    [
      projectRole?.regionalProjectRoles,
      originalProjectRole?.regionalProjectRoles,
      regions,
      originalRegions,
    ]
  );

  useEffect(() => {
    handleUnsavedChanges(hasRegionalChanges);
  }, [hasRegionalChanges, handleUnsavedChanges]);

  const onMenuClick = ({ key }: { key: string }): void => {
    setSelectedRegion({
      key: key,
      value: getPropertyValueDisplay(FormType.Intake, FieldNames.Region, key),
    });
    setModalOpen(true);
  };

  const addRegionalRole = (functionalArea: string): void => {
    const newRole: IRegionalProjectRole = {
      defaultProjectUsers: [],
      hasDefaultUsers: false,
      hasLimitedList: false,
      intakeApprovers: [],
      isDeleted: false,
      limitedListUsers: [],
      projectTeams: [],
      id: v4(),
      region: selectedRegion.key,
      functionalArea: functionalArea,
    };

    const newRegionRoles = [
      newRole,
      ...(projectRole.regionalProjectRoles ?? []),
    ];

    updateProjectRole({
      ...projectRole,
      regionalProjectRoles: newRegionRoles,
    });
    setModalOpen(false);
  };

  const handleDelete = (id: string): void => {
    const newRegionRoles =
      projectRole.regionalProjectRoles?.filter(
        (regionRole) => regionRole.id !== id
      ) ?? [];

    updateProjectRole({ ...projectRole, regionalProjectRoles: newRegionRoles });
  };

  return (
    <>
      <Row gutter={GUTTER_SIZE.large}>
        <Col span={CONTENT_WIDTH.half}>
          <ProjectRoleNameEditor
            role={projectRole}
            originalRole={originalProjectRole}
            handleUnsavedChanges={handleUnsavedChanges}
          />
        </Col>
        <Col span={CONTENT_WIDTH.half}>
          <ProjectRoleHistory role={projectRole} />
        </Col>
      </Row>

      <Row className="p-2 mt-2">
        <Dropdown
          menu={{ items: regions, onClick: onMenuClick }}
          data-test-id="add-regional-role"
        >
          <Button data-test-id="add-regional-role-button">
            <Space>
              {intl.formatMessage({ id: "projectRoles.addRegionalRole" })}
              <FontAwesomeIcon icon={faChevronDown as IconProp} />
            </Space>
          </Button>
        </Dropdown>
      </Row>

      <Row>
        {projectRole?.regionalProjectRoles?.map(
          (regionRole): ReactNode => (
            <RegionalRoleEditor
              key={regionRole.id}
              role={projectRole}
              regionRole={regionRole}
              deleteRole={handleDelete}
            />
          )
        )}
      </Row>

      <AddRegionalRoleModal
        open={modalOpen}
        projectRole={projectRole}
        selectedRegion={selectedRegion}
        formFieldOptions={forms?.intake?.functionalArea ?? {}}
        onClose={() => setModalOpen(false)}
        onOk={addRegionalRole}
      />
    </>
  );
};
